import type { SourceType } from "../types/visibility";

interface SourceTypeMeta {
  label: string;
  description: string;
  chipColor: string;
}

const SOURCE_TYPE_META: Record<SourceType, SourceTypeMeta> = {
  encyclopedia: {
    label: "Encyclopedia",
    description: "General reference sites such as Wikipedia.",
    chipColor: "blue",
  },
  community: {
    label: "Community",
    description: "Forums and community-written discussion, e.g. Reddit.",
    chipColor: "purple",
  },
  review_platform: {
    label: "Review Platform",
    description: "User review sites such as TripAdvisor or Yelp.",
    chipColor: "yellow",
  },
  ota: {
    label: "Booking Site",
    description: "Online travel agencies and booking platforms.",
    chipColor: "emerald",
  },
  directory: {
    label: "Directory",
    description: "Business or destination listing directories.",
    chipColor: "neutral",
  },
  video: {
    label: "Video",
    description: "Video platforms such as YouTube.",
    chipColor: "red",
  },
  social: {
    label: "Social Media",
    description: "Social media posts and profiles.",
    chipColor: "orange",
  },
  news: {
    label: "News",
    description: "News publications and press coverage.",
    chipColor: "blue",
  },
  listicle: {
    label: "Listicle",
    description: '"Best of" roundup and ranking articles.',
    chipColor: "purple",
  },
  own_site: {
    label: "Own Site",
    description: "The business's own website.",
    chipColor: "emerald",
  },
  other: {
    label: "Other",
    description: "Source type not covered by the taxonomy above.",
    chipColor: "neutral",
  },
};

const FALLBACK_META: SourceTypeMeta = {
  label: "Other",
  description: "Source type not covered by the taxonomy above.",
  chipColor: "neutral",
};

/** Turns a raw (possibly unknown, future) source_type value into readable text. */
export const humanize = (sourceType: string): string => {
  const meta = SOURCE_TYPE_META[sourceType as SourceType];
  if (meta) return meta.label;
  return sourceType
    .split("_")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
};

export const getSourceTypeDescription = (sourceType: string): string =>
  (SOURCE_TYPE_META[sourceType as SourceType] || FALLBACK_META).description;

export const getSourceTypeChipColor = (sourceType: string): string =>
  (SOURCE_TYPE_META[sourceType as SourceType] || FALLBACK_META).chipColor;
